import React, { useCallback, useContext, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button, Typography } from "@mui/material";
import useAuthorization from "../../../custom-hooks/useAuthorization/useAuthorization";
import { TnAOverviewsFilter } from "../../../models/tna-overviews/tna-overview.filter";
import { TnAWorkedTimesApiService } from "../../../services/tna-worked-times/tna-worked-times-api.service";
import { TnAWorkedTimeTotalsApiService } from "../../../services/tna-worked-time-totals/tna-worked-time-totals-api.service";
import { AUTHORIZATIONS } from "../../../utils/constants/authorization-constants";
import { openTimesheetViewMode } from "../../../views/my-t-and-a/my-ee-tna-utils";
import QuickfiltersContainer from "../../Quickfilters/QuickfiltersContainer/QuickfiltersContainer";
import { MyTnAContext } from "../MyTeamPresenceAndLeaves";
import { MainTableContainer } from "./styles";
import { EmployeeRef } from "../../../models/employees/employee-ref";
import LoadingOverlay from "../../../containers/LoadingOverlay/LoadingOverlay";
import { EmployeeGroupsApiService } from "../../../services/employee-groups/employee-groups-api.service";
import { RefModel } from "../../../models/base/ref-model";
import ExportTnAWorkedTimeToExcel from "./ExportTnAWorkedTimeToExcel/ExportTnAWorkedTimeToExcel";
import WorkedTimeTotalsView from "./WorkedTimeTotalsView/WorkedTimeTotalsView";
import HeaderPortal from "../../../containers/TableHeader/HeaderPortal";
import WorkedTimeTableView from "./WorkedTimeTableView/WorkedTimeTableView";
import { DTOModel } from "../../../models/base/dto-model";

interface State {
  loading: boolean;
  error: string;
}

const TnAWorkedTime = () => {
  const { t } = useTranslation();
  const tRPTitle = "components.team_presence_and_leaves_worked_time.table_title";

  const hasAccess = useAuthorization([AUTHORIZATIONS.EmployeesAccess.ApproveTimeAndLeaves]);

  const allowedToViewOnly = useAuthorization([AUTHORIZATIONS.EmployeesAccess.ViewNonSalaryAndCompensationData]);

  const {
    legalEntity: { legalEntityOption, legalEntityQuickFilter },
    employeeGroup: { employeeGroupOption, employeeGroupQuickFilter },
    currentEmployees: { onlyCurrentEmployeesOptionValue, onlyCurrentEmployeesQuickFilter },
    searchingDate: { year, month, yearMonthQuickFilter },
  } = useContext(MyTnAContext);

  const navigate = useNavigate();

  const allItems = useRef<DTOModel[]>([]);

  const [isTotalsView, setIsTotalsView] = useState<boolean>(false);

  const [state, setState] = useState<State>({
    loading: false,
    error: "",
  });

  const navigateToTimesheet = (employee: EmployeeRef | null) => {
    if (!hasAccess && allowedToViewOnly && employee?.id) {
      openTimesheetViewMode(employee.id);
    }

    if (hasAccess) {
      navigate("/my-t-and-a", {
        state: {
          employee,
          forManager: true,
        },
      });
    }
  };

  const loadEmployeeGroup = useCallback(async () => {
    try {
      const response = await new EmployeeGroupsApiService().loadItems({
        ids: employeeGroupOption?.employeeGroupIds,
      });

      if (!response.error) {
        return response.items[0];
      }
    } catch (error) {
      setState((state) => ({
        ...state,
        error: error as string,
      }));
    }
  }, [employeeGroupOption?.employeeGroupIds]);

  const loadTnAWorkedTimeData = useCallback(
    async (pageSize: number = 10) => {
      try {
        const response = await new TnAWorkedTimesApiService().loadItems(
          {
            employeeGroupIds: employeeGroupOption?.employeeGroupIds,
            legalEntityIds: legalEntityOption?.legalEntityIds,
            onlyCurrentEmployees: !!onlyCurrentEmployeesOptionValue?.onlyCurrentEmployees,
            year: year,
            month: month + 1,
          } as TnAOverviewsFilter,
          "",
          [],
          {
            pageNumber: 0,
            pageSize: pageSize,
          }
        );

        if (!response.error) {
          return response;
        }
      } catch (error) {
        setState((state) => ({
          ...state,
          error: error as string,
        }));
      }
    },
    [
      employeeGroupOption?.employeeGroupIds,
      onlyCurrentEmployeesOptionValue?.onlyCurrentEmployees,
      legalEntityOption?.legalEntityIds,
      year,
      month,
    ]
  );

  const loadTnAWorkedTimeTotalsData = useCallback(
    async (pageSize: number = 10) => {
      try {
        const response = await new TnAWorkedTimeTotalsApiService().loadItems(
          {
            employeeGroupIds: employeeGroupOption?.employeeGroupIds,
            legalEntityIds: legalEntityOption?.legalEntityIds,
            onlyCurrentEmployees: !!onlyCurrentEmployeesOptionValue?.onlyCurrentEmployees,
            year: year,
            month: month + 1,
          } as TnAOverviewsFilter,
          "",
          [],
          {
            pageNumber: 0,
            pageSize: pageSize,
          }
        );

        if (!response.error) {
          return response;
        }
      } catch (error) {
        setState((state) => ({
          ...state,
          error: error as string,
        }));
      }
    },
    [
      employeeGroupOption?.employeeGroupIds,
      onlyCurrentEmployeesOptionValue?.onlyCurrentEmployees,
      legalEntityOption?.legalEntityIds,
      year,
      month,
    ]
  );

  const quickfilters = useCallback(
    (items: RefModel[], loading: boolean) => {
      return (
        <QuickfiltersContainer>
          <>
            {!!legalEntityQuickFilter && legalEntityQuickFilter}
            {!!employeeGroupQuickFilter && employeeGroupQuickFilter}
            {!!onlyCurrentEmployeesQuickFilter && onlyCurrentEmployeesQuickFilter}
            {!!yearMonthQuickFilter && yearMonthQuickFilter}
            <ExportTnAWorkedTimeToExcel
              loadEmployeeGroup={loadEmployeeGroup}
              loadTnAWorkedTimeData={loadTnAWorkedTimeData}
              loadTnAWorkedTimeTotalsData={loadTnAWorkedTimeTotalsData}
              disabled={state.loading || loading || !items?.length}
              allItems={() => allItems.current}
              isTotalsView={isTotalsView}
            />
          </>
        </QuickfiltersContainer>
      );
    },
    [state.loading, legalEntityQuickFilter, employeeGroupQuickFilter, onlyCurrentEmployeesQuickFilter, isTotalsView]
  );

  return (
    <LoadingOverlay
      loading={state.loading}
      error={state.error}
    >
      <MainTableContainer>
        {(hasAccess || allowedToViewOnly) && (
          <>
            <HeaderPortal
              title={`${t(`${tRPTitle}`)}${isTotalsView ? " totals" : ""}`}
              buttonToRender={
                <Button
                  variant="contained"
                  onClick={() => setIsTotalsView(!isTotalsView)}
                  style={{
                    textTransform: "none",
                    marginTop: "5px",
                    padding: "5px",
                    width: "fit-content",
                  }}
                >
                  <Typography variant="body2">
                    {isTotalsView ? "Change to table view" : "Change to totals view"}
                  </Typography>
                </Button>
              }
            />

            {!isTotalsView && (
              <WorkedTimeTableView
                navigateToTimesheet={navigateToTimesheet}
                quickfilters={quickfilters}
                getAllItems={(items) => {
                  allItems.current = items as DTOModel[];
                }}
              />
            )}

            {isTotalsView && (
              <WorkedTimeTotalsView
                navigateToTimesheet={navigateToTimesheet}
                quickfilters={quickfilters}
                getAllItems={(items) => {
                  allItems.current = items as DTOModel[];
                }}
              />
            )}
          </>
        )}
      </MainTableContainer>
    </LoadingOverlay>
  );
};

export default TnAWorkedTime;
